<template>
  <div class='organizations-settings-row'>
    <div class='organizations-settings-row__content'>
      <div class="main-info" @click="isOpen = !isOpen">
        <div class="name-column">
          <div class='name-column__logo'>
            <v-avatar>
              <img v-if="logo"
                   class="avatar" :src="(logo)" alt=''>
              <v-icon v-else class="initials">{{initials}}</v-icon>
            </v-avatar>
          </div>
          <div class="name-column__data">
            <custom-tooltip class="name-column__data__tooltip" popper-class="light-tooltip">
              <div slot='buttonContent' class='name-column__data__tooltip__content'>
                {{ name }} <span class='default' v-if='isDefaultOrganization'> {{ $t('default') }} </span>
              </div>
              <div slot="content">
                <slot name="tooltip">
                  {{ name }}
                </slot>
              </div>
            </custom-tooltip>
          </div>
        </div>
        <div class='count-column'>
          <div class="count-column__data domains">
            <div class='box'>
              <div class='count-column__data__icon-separator'>
                <v-icon>language</v-icon>
              </div>
              <div class='count-column__data__domains-stats'>
                {{ domainsCount }}
              </div>
            </div>
          </div>
        </div>
        <div class='count-column'>
          <div class="count-column__data cloud">
            <div class='box'>
              <div class='count-column__data__icon-separator'>
                <v-icon>cloud_queue</v-icon>
              </div>
              <div class='count-column__data__domains-stats'>
                {{ cloudAccountCount }}
              </div>
            </div>
          </div>
        </div>
        <div class='date-column'>
          <div class="date-column__data">
            {{ dateCreated }}
          </div>
        </div>
        <div class='actions-column'>
          <animation-tooltip-wrapper
            animation-class-name='edit-animation'
            btn-class-name='organization-edit'
            refName='editAnimation'
            :animationFile='require(`@/assets/icons/animations/edit.json`)'
            :tooltip-text='$t("edit.tooltip").toString()'
            @clicked='editOrganization'
          />
          <animation-tooltip-wrapper
            animation-class-name='link-animation'
            btn-class-name='organization-link-domain'
            refName='linkAnimation'
            :animationFile='require(`@/assets/icons/animations/domain-link.json`)'
            :tooltip-text='$t("link.domains.tooltip").toString()'
            @clicked='linkDomainsToOrganization'
          />
          <animation-tooltip-wrapper
            animation-class-name='change-default-domain-animation'
            btn-class-name='change-default-domain'
            refName='changeDefaultDomainAnimation'
            :animationFile='require(`@/assets/icons/animations/change-default.json`)'
            :tooltip-text='$t("change.default.domain.tooltip").toString()'
            @clicked='setDefaultDomain'
          />
          <animation-tooltip-wrapper
            animation-class-name='delete-animation'
            btn-class-name='organization-delete'
            refName='deleteAnimation'
            :disabled='isDefaultOrganization'
            :animationFile='require(`@/assets/icons/animations/delete.json`)'
            :tooltip-text='$t("delete.tooltip").toString()'
            @clicked='deleteOrganization'
          />
        </div>
        <div class='right-section' v-if='linkedDomains.length'>
          <custom-tooltip class="right-section__tooltip" popper-class="light-tooltip">
            <div slot='buttonContent' class='right-section__tooltip__content'>
              <div class='arrow-expand-section'>
                <v-icon class="expand-icon" :class="{ expand: isOpen }">expand_more</v-icon>
              </div>
            </div>
            <div slot="content">
              <slot name="tooltip">
                {{ $t('linked.assets.tooltip') }}
              </slot>
            </div>
          </custom-tooltip>
        </div>
      </div>
      <div class="domains-container" v-if="isOpen">
        <organization-linked-domains-list
          :items='linkedDomains'
          :enable-unlink='organization.name !== companyData.name'
          :organization='organization'
          @unlinkDomain='unlinkDomain'
        />
      </div>
    </div>
    <div class='organizations-settings-row__tags'>
      <div class='organizations-settings-row__tags__tags-list'>
        <v-chip
          label
          outlined
          close
          pill
          text-color='#2459d5'
          small
          class='tag'
          v-for='(tagKey, index) in nonExcludedTagKeys'
          :key='index'
          close-icon='close'
          @click:close='removeTagFromList(tagKey)'
        >
          {{ tagKey }}
        </v-chip>
      </div>
      <span class='no-tags' v-if='nonExcludedTagKeys.length === 0'>
        {{ $t('no.tags') }}
      </span>
      <span class="material-icons" @click='openAddTagPopup'>
        local_offer
      </span>
    </div>
  </div>
</template>

<script>

import CustomTooltip from '@/components/common/CustomTooltip.vue';
import OrganizationLinkedDomainsList from '@/components/Settings/OrganizationLinkedDomainsList.vue';
import { formatDateToShort } from '@/utils/dateUtils';
import AnimationTooltipWrapper from '@/components/common/AnimationTooltipWrapper.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'OrganizationsSettingsRow',
  components: {
    AnimationTooltipWrapper, OrganizationLinkedDomainsList, CustomTooltip,
  },
  props: {
    organization: {
      required: true,
      type: Object,
    },
    linkedDomains: {
      required: true,
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isOpen: false,
    };
  },
  methods: {
    async setDefaultDomain() {
      this.$emit('setDefaultDomain', this.organization);
    },
    unlinkDomain(payload) {
      this.$emit('unlinkDomain', payload);
    },
    editOrganization() {
      this.$emit('editOrganization', this.organization);
    },
    deleteOrganization() {
      this.$emit('deleteOrganization', this.organization);
    },
    linkDomainsToOrganization() {
      this.$emit('linkDomainsToOrganization', this.organization);
    },
    removeTagFromList(tagKey) {
      this.$emit('removeTag', { organization: this.organization, tag: tagKey });
    },
    openAddTagPopup() {
      this.$emit('openAddTagPopup', this.organization);
    },
  },
  computed: {
    ...mapGetters(['companyData', 'organizationsTags', 'configurationCloudAccounts', 'configurationDomains']),
    currentOrganizationsTags() {
      return this.organizationsTags?.[this.organization.name] || {};
    },
    nonExcludedTagKeys() {
      return this.tagKeys.filter(key => this.tags[key] !== 'exclude');
    },
    tagKeys() {
      return Object.keys(this.tags);
    },
    tags() {
      return { ...this.currentOrganizationsTags };
    },
    isDefaultOrganization() {
      return this.organization.name === this.companyData.name;
    },
    name() {
      return this.organization.name;
    },
    domainsCount() {
      return this.configurationDomains.filter(domain => domain.organization.name === this.organization.name)?.length || 0;
    },
    cloudAccountCount() {
      return this.configurationCloudAccounts.filter(account => account.company.name === this.organization.name)?.length || 0;
    },
    dateCreated() {
      return formatDateToShort(this.organization.createdDate);
    },
    logo() {
      if (!this.organization?.logoURL || this.organization?.logoURL?.startsWith('data')) {
        return this.organization?.logoURL;
      }
      return `${this.organization?.logoURL}?${Date.now().toString()}` || null;
    },
    initials() {
      if (this.organization.name) {
        return this.organization.name[0].toUpperCase();
      }
      return null;
    },
  },
};
</script>

<style lang='scss'>
@import '~@/assets/styles/design-system';
.organization-delete, .organization-add-tags, .organization-link-domain, .change-default-domain {
  margin-left: rem(15);
}
.organization-delete, .organization-edit, .organization-link-domain, .change-default-domain {
  z-index: 10;
  align-items: center;
  background-color: var(--btn-good-bg) !important;
  border-radius: $radius-xs;
  cursor: pointer;
  display: flex;
  height: rem(32);
  justify-content: center;
  width: rem(32);
  min-width: rem(32);
  min-height: rem(32);
  color: var(--brand-primary) !important;
  &:hover {
    cursor: pointer;
    border: 1px solid var(--btn-good-border-color);
    background-color: var(--btn-good-hover-bg) !important;
  }
  &:active{
    border: 1px solid var(--btn-good-border-color);
    background-color: var(--btn-good-hover-bg) !important;
  }
  &.disabled {
    opacity: 0.5;
    border: 1px solid var(--btn-good-border-color);
    background-color: transparent !important;
  }
  .edit-animation, .delete-animation, .link-animation, .change-default-domain-animation {
    height: rem(30);
  }
  .link-animation, .change-default-domain-animation {
    margin-left: 8%;
    width: 81% !important;
  }
}
.organization-delete {
  margin-left: rem(15);
  background-color: var(--btn-action-bg) !important;
  &:hover {
    cursor: pointer;
    border: 1px solid var(--btn-action-border-color);
    background-color: var(--btn-action-hover-bg) !important;
  }
  &:active{
    border: 1px solid var(--btn-action-border-color);
    background-color: var(--btn-action-hover-bg) !important;
  }
  &.disabled {
    opacity: 0.5;
    border: 1px solid rgba(179,49,207,0.1);
    background-color: rgba(155, 176, 190, 0.35) !important;
  }
}

[data-theme="dark"] {
  .organization-edit, .organization-link-domain, .change-default-domain {
    background-color: var(--btn-good-bg) !important;
    border: 1px solid transparent;
    &:hover {
      background-color: var(--btn-good-hover-bg) !important;
      border-color: var(--btn-good-border-color);
    }
    &:active {
      background-color: var(--btn-good-hover-bg) !important;
      border-color: var(--btn-good-border-color);
    }
  }
  .organization-delete {
    background-color: var(--btn-action-bg) !important;
    border: 1px solid transparent;
    &:hover {
      background-color: var(--btn-action-hover-bg) !important;
      border-color: var(--btn-action-border-color);
    }
    &:active {
      background-color: var(--btn-action-hover-bg) !important;
      border-color: var(--btn-action-border-color);
    }
    &.disabled {
      background-color: rgba(148, 163, 184, 0.25) !important;
    }
  }
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';
.organizations-settings-row {
  .v-chip.v-chip--outlined.v-chip.v-chip {
    background-color: var(--bg-tertiary) !important;
    border-color: var(--border-primary) !important;
    padding: rem(5) !important;
    font-size: rem(12) !important;
  }
  .v-chip.v-chip--outlined .v-icon {
    font-size: rem(14) !important;
    color: var(--text-primary) !important;
  }
}
</style>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';
.organizations-settings-row {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  min-height: rem(58);
  border-bottom: 1px solid var(--border-primary);
  padding: rem(5) 0;

  img.avatar {
    width: rem(42);
    height: rem(42);
    margin-right: 0;
  }

  ::v-deep .v-avatar {
    min-width: rem(42) !important;
    width: rem(42) !important;
    min-height: rem(42) !important;
    height: rem(42) !important;
    margin: rem(8) rem(8) rem(8) 0;
  }

  .initials {
    background-color: rgba(14,31,51,0.1);
    border-radius: rem(50);
    color: var(--brand-primary);
    display: block;
    height: rem(42);
    width: rem(45);
    font-size: rem(16);
    font-weight: bolder;
    font-family: $font-family-regular;
    padding-top: rem(13);
  }
  &__tags {
    display: flex;
    align-self: end;
    align-items: center;
    margin-top: 0.5%;
    margin-bottom: 0.5%;
    &__tags-list {
      margin-right: rem(10);
      .tag {
        margin-right: rem(8);
      }
    }
    .no-tags {
      margin-right: rem(5);
      color: var(--text-tertiary);
      font-family: $font-family-regular;
      font-size: rem(12);
      letter-spacing: rem(1.1);
      line-height: rem(15);
    }
    .material-icons {
      color: var(--text-secondary);
      font-size: rem(18);
      &:hover {
        cursor: pointer;
        color: var(--brand-primary);
      }
    }
  }
  &__content {
    width: 100%;
    margin-top: 0.5%;
    .main-info {
      align-items: center;
      display: flex;
      .name-column {
        align-items: center;
        cursor: pointer;
        display: flex;
        width: 20%;
        flex-direction: row;
        justify-content: left;
        &__logo {
          .v-avatar {
            width: rem(42) !important;
            height: rem(42) !important;
            border: 1px solid var(--border-primary);
          }
        }
        &__data {
          margin-right: 2.5%;
          &__tooltip {
            &__content {
              text-transform: capitalize;
              color: var(--text-primary);
              font-family: $font-family-medium;
              font-size: rem(14);
              .default {
                font-size: rem(10);
                margin-left: rem(2);
                color: var(--text-secondary);
              }
            }
          }
        }
      }
      .count-column {
        width: 20%;
        align-items: center;
        cursor: pointer;
        display: flex;
        flex-direction: row;
        justify-content: left;
        font-family: $font-family-semibold;
        &__data {
          display: flex;
          flex-direction: row;
          align-items: center;
          font-size: rem(16);
          font-weight: 600;
          letter-spacing: 0;
          &.cloud {
            margin-left: rem(70);
          }
          &.domains {
            margin-left: rem(34);
          }
          .box {
            align-items: center;
            background-color: var(--status-unresolved-bg);
            display: flex;
            flex-direction: row;
            padding: rem(6) rem(3);
            border-radius: $radius-xs;
            text-align: left;
          }
          &__domains-stats {
            font-size: rem(16);
            color: var(--text-primary);
          }
          &__icon-separator {
            margin-bottom: rem(2);
            margin-right: rem(5);
            .v-icon {
              color: var(--status-unresolved);
              font-size: rem(18);
              font-weight: 400;
            }
          }
        }
      }
      .date-column {
        width: 27%;
        align-items: center;
        cursor: pointer;
        display: flex;
        flex-direction: row;
        justify-content: left;
        &__data {
          margin-left: rem(7);
        }
      }
      .actions-column {
        width: 10%;
        display: flex;
      }
      .right-section {
        display: flex;
        width: 3%;
        position: absolute;
        right: 3.5%;
        .arrow-expand-section {
          display: flex;
          justify-content: flex-end;
        }
      }
    }
  }
}
</style>
